import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../CommonStyles/commonstyles.module.scss";
import noImageFound from "../../../../assets/images/extras/noImgFound.png";
import uploadedImage from "../../../../assets/images/svg/noData.png";
import DeleteBox from "../../../../components/DeleteBox/DeleteBox";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBin5Line } from "react-icons/ri";
import {
  useDeleteEquipment,
  useFetchAllEquipments,
} from "../../../../hooks/partnerHook/usePartnerEquipment";
import { updateEquipmentsData } from "../../../../store/Partner/partnerDataSlice";

const Equipments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.partner.user);
  const partnerId = userData?.partner?.id;

  const {
    data: equipmentsData,
    isPending,
    isError,
    refetch, // Add refetch from useFetchAllEquipments
  } = useFetchAllEquipments(partnerId);

  const {
    mutate: deleteEquipment,
    isSuccess: equipmentDeleted,
    isError: deletionError,
  } = useDeleteEquipment();

  const [equipments, setEquipments] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");

  // Update Redux store with fetched data
  useEffect(() => {
    if (equipmentsData) {
      dispatch(updateEquipmentsData(equipmentsData));
    }
  }, [dispatch, equipmentsData]);

  // Force re-fetch on mount
  useEffect(() => {
    if (partnerId) {
      refetch(); // Trigger a re-fetch when the component mounts
    }
  }, [partnerId, refetch]);

  const savedEquipments = useSelector((state) => state.partner.equipments);
  useEffect(() => {
    console.log("Saved Equipments:", savedEquipments);
    if (savedEquipments) {
      setEquipments(savedEquipments);
      setIsDataLoading(false);
    }
  }, [savedEquipments]);

  const handleDelete = () => {
    if (deleteId) {
      deleteEquipment(
        { partnerId, equipmentId: deleteId },
        {
          onSuccess: () => {
            refetch(); // Re-fetch after deletion
          },
        }
      );
      setShowDeleteModal(false);
    }
  };

  const handleNavigateToDetails = (equipmentId) => {
    navigate(`/dashboard/equipments/${equipmentId}`);
  };

  if (isPending || isDataLoading) {
    return <h2>Loading...</h2>;
  }

  if (isError) {
    return <h2>Error! Please refresh and try again.</h2>;
  }

  return (
    <div className={styles.dashboardContainer}>
      <h2 className={styles.browseEquipmentsTitle}>Browse All Equipments</h2>

      {equipments.length > 0 ? (
        <div className={styles.dashboardGridContainer}>
          {equipments.map((equipment) => (
            <div
              key={equipment.equipment_id}
              className={styles.dashboardCardWrapper}
              onClick={() => handleNavigateToDetails(equipment.equipment_id)}
            >
              <div className={styles.dashboardCard}>
                <div className={styles.dashboardCardImageContainer}>
                  <img
                    src={
                      equipment.media.length > 0
                        ? equipment.media[0].media_url
                        : noImageFound
                    }
                    alt={equipment.title}
                    className={styles.droneCardImg}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = noImageFound;
                    }}
                  />
                </div>
                <div className={styles.dashboardCardBody}>
                  <div className={styles.dashboardCardStack}>
                    <h5 className={styles.dashboardCardCategoryButton}>
                      {equipment.equipment_type?.equipment_category ||
                        equipment.equipment_category ||
                        "Loading..."}
                    </h5>
                    <h5
                      className={`${styles.dashboardCardVerifiedButton} ${
                        equipment.verification_status === "pending"
                          ? styles.pending
                          : equipment.verification_status === "success"
                          ? styles.success
                          : styles.rejected
                      }`}
                    >
                      {equipment.verification_status === "pending"
                        ? "Requested to verify"
                        : equipment.verification_status === "success"
                        ? "Verified"
                        : "Rejected"}
                    </h5>
                  </div>
                  <div className={styles.equipmentHeader}>
                    <h5 className={styles.dashboardCardCategoryTitle}>
                      {equipment.title || "N/A"}
                    </h5>
                    <RiDeleteBin5Line
                      className={styles.deleteIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(equipment.equipment_id);
                        setDeleteName(equipment.title);
                        setShowDeleteModal(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.noDataSection}>
          <img
            src={uploadedImage}
            alt="No Data"
            className={styles.noDataImage}
          />
          <h4 className={styles.noEquipmentText}>No equipment added</h4>
          <p className={styles.noEquipmentDescription}>
            Provide details about your drones, cameras, sensors, and other
            equipment.
          </p>
        </div>
      )}

      {showDeleteModal && (
        <DeleteBox
          deleteFunction={handleDelete}
          isSuccess={equipmentDeleted}
          isError={deletionError}
          contentName={deleteName}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}
    </div>
  );
};

export default Equipments;
